import { useMutation, useQueryClient } from "@tanstack/react-query";
import { propertiesApi } from "../../../libs/landvoice-api";
import { Listing } from "../../../types/listing";
import {
  LISTING_DETAILS_QUERY_KEY,
  SEARCH_LISTING_QUERY_KEY,
} from "../../../constants/cache";

const useFavoriteMutation = (isFavorite: boolean) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (propertyId: string) => {
      const response = isFavorite
        ? await propertiesApi.addFavorite(propertyId)
        : await propertiesApi.removeFavorite(propertyId);
      return response.data;
    },
    onMutate: async (propertyId) => {
      await queryClient.cancelQueries({
        predicate: (query) => query.queryKey[0] === SEARCH_LISTING_QUERY_KEY,
      });
      await queryClient.cancelQueries({
        queryKey: [LISTING_DETAILS_QUERY_KEY],
      });

      const previousListings = queryClient.getQueriesData({
        predicate: (query) => query.queryKey[0] === SEARCH_LISTING_QUERY_KEY,
      });
      const previousDetails = queryClient.getQueryData([
        LISTING_DETAILS_QUERY_KEY,
      ]);

      // Update all matching search listing queries
      queryClient
        .getQueriesData({
          predicate: (query) => query.queryKey[0] === SEARCH_LISTING_QUERY_KEY,
        })
        .forEach(([queryKey, data]) => {
          if (!data) return;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          queryClient.setQueryData(queryKey, (old: any) => {
            if (!old) return old;
            // Handle infinite query data structure
            if ("pages" in old) {
              return {
                ...old,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                pages: old.pages.map((page: any) => ({
                  ...page,
                  listings: page.listings?.map((listing: Listing) =>
                    listing.propertyId === propertyId
                      ? { ...listing, isFavorite }
                      : listing
                  ) || []
                })),
              };
            }
            // Handle regular query data structure with { listings: [], total_records: number }
            if (old.listings && Array.isArray(old.listings)) {
              return {
                ...old,
                listings: old.listings.map((listing: Listing) =>
                  listing.propertyId === propertyId
                    ? { ...listing, isFavorite }
                    : listing
                )
              };
            }
            // Fallback for direct array structure (if any queries still use this)
            if (Array.isArray(old)) {
              return old.map((listing: Listing) =>
                listing.propertyId === propertyId
                  ? { ...listing, isFavorite }
                  : listing
              );
            }
            return old;
          });
        });

      // Update listing details cache
      const detailsQueries = queryClient.getQueriesData({
        queryKey: [LISTING_DETAILS_QUERY_KEY],
      });
      detailsQueries.forEach(([queryKey, data]) => {
        const listing = data as Listing;
        if (listing?.propertyId === propertyId) {
          queryClient.setQueryData(queryKey, {
            ...listing,
            isFavorite,
          });
        }
      });

      return { previousListings, previousDetails };
    },
    onError: (err, propertyId, context) => {
      if (context?.previousListings) {
        context.previousListings.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousDetails) {
        queryClient.setQueryData(
          [LISTING_DETAILS_QUERY_KEY],
          context.previousDetails
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === SEARCH_LISTING_QUERY_KEY,
      });
      queryClient.invalidateQueries({ queryKey: [LISTING_DETAILS_QUERY_KEY] });
    },
  });
};

export const useAddFavoriteMutation = () => useFavoriteMutation(true);
export const useRemoveFavoriteMutation = () => useFavoriteMutation(false);